import { httpClient, MAX_PAGE_SIZE, type HttpClient, type Paged } from '../core'
import { assetsEndpoints } from './assets.endpoints'
import { z } from 'zod'

import { UPLOAD_FIELDS, projectAssetPageSchema, uploadResponseSchema, type AssetAnalysisUpdate, type ProjectAssetDto, type UploadFiles, type UploadResponseDto } from './assets.schemas'

/** A safety stop, so a server that ignores `page` can never keep `listAll` running. */
const MAX_PAGES = 50

export class AssetsService {
  private readonly http: HttpClient

  constructor(http: HttpClient = httpClient) {
    this.http = http
  }

  /** One page of a project's files. */
  list(projectId: string, params: { page?: number; pageSize?: number } = {}, signal?: AbortSignal): Promise<Paged<ProjectAssetDto>> {
    return this.http.get(assetsEndpoints.collection(projectId), projectAssetPageSchema, { query: params, signal })
  }

  /** Every file of a project, page by page, as one array. */
  async listAll(projectId: string, signal?: AbortSignal): Promise<ProjectAssetDto[]> {
    const items: ProjectAssetDto[] = []
    for (let page = 1; page <= MAX_PAGES; page++) {
      const result = await this.list(projectId, { page, pageSize: MAX_PAGE_SIZE }, signal)
      items.push(...result.items)
      if (result.items.length === 0 || items.length >= result.totalCount) break
    }
    return items
  }

  /** Saves edited analyses of a project's files in one request. The answer is not used: the screen keeps what it sent. */
  async updateAnalyses(projectId: string, updates: readonly AssetAnalysisUpdate[], signal?: AbortSignal): Promise<void> {
    await this.http.put(assetsEndpoints.collection(projectId), z.unknown(), { body: updates.map((update) => ({ ...update, projectId })), signal })
  }

  /**
   * Sends a project's source files in ONE multipart request. The project id is in the path, never in the form. The answer says
   * how many files arrived and names the task that processes them. Not retried automatically: a lost request may still have
   * worked, and a second upload would duplicate the files.
   */
  upload(projectId: string, files: UploadFiles, signal?: AbortSignal): Promise<UploadResponseDto> {
    const form = new FormData()
    for (const field of UPLOAD_FIELDS) for (const file of files[field] ?? []) form.append(field, file, file.name)
    return this.http.post(assetsEndpoints.upload(projectId), uploadResponseSchema, { body: form, signal, timeoutMs: 5 * 60_000 })
  }
}

export const assetsService = new AssetsService()
